/**
 * Property Filters Module
 *
 * Advanced filter system with custom dropdowns and range sliders.
 *
 * @package TheRealEstates
 */

export default class PropertyFilters {
  constructor() {
    // Off-canvas filter panel elements
    this.filterPanel = document.querySelector('[data-filter-panel]');
    this.filterOverlay = document.querySelector('[data-filter-overlay]');
    this.filterOpenBtn = document.querySelector('[data-filter-open]');
    this.filterCloseBtn = document.querySelector('[data-filter-close]');
    this.filterForm = document.querySelector('[data-filter-form]');

    // Other controls
    this.sortControl = document.querySelector('[data-sort-control]');
    this.viewButtons = document.querySelectorAll('[data-view]');
    this.propertyGrid = document.querySelector('[data-property-grid]');
    this.propertyArchive = document.querySelector('.property-archive');
    this.activeFiltersSection = document.querySelector('[data-active-filters]');
    this.filterChipsContainer = document.querySelector('[data-filter-chips]');
    this.shareButtons = document.querySelectorAll('[data-share-copy]');
    this.printButtons = document.querySelectorAll('[data-share-print]');

    // Default filter values
    this.defaults = {
      min_price: 500000,
      max_price: 10000000,
      min_area: 100,
      max_area: 2000,
    };

    this.init();
  }

  init() {
    this.bindEvents();
    this.restoreViewMode();
    this.initOffCanvasPanel();
    this.initCustomDropdowns();
    this.initRangeSliders();
    this.initFilterChips();
    this.initAjaxFiltering();
  }

  bindEvents() {
    // Sort control
    this.sortControl?.addEventListener('change', (e) => {
      this.handleSort(e.target.value);
    });

    // View mode toggle
    this.viewButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.setViewMode(btn.dataset.view);
      });
    });

    // Share buttons
    this.shareButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.copyToClipboard(btn.dataset.url);
      });
    });

    // Print buttons
    this.printButtons.forEach((btn) => {
      btn.addEventListener('click', () => window.print());
    });
  }

  // =====================================================
  // OFF-CANVAS FILTER PANEL
  // =====================================================

  initOffCanvasPanel() {
    if (!this.filterPanel) return;

    // Open button click
    this.filterOpenBtn?.addEventListener('click', () => {
      this.openFilterPanel();
    });

    // Close button click
    this.filterCloseBtn?.addEventListener('click', () => {
      this.closeFilterPanel();
    });

    // Overlay click to close
    this.filterOverlay?.addEventListener('click', () => {
      this.closeFilterPanel();
    });

    // Close on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.filterPanel?.classList.contains('is-open')) {
        this.closeFilterPanel();
      }
    });
  }

  openFilterPanel() {
    if (!this.filterPanel) return;

    this.filterPanel.classList.add('is-open');
    this.filterOverlay?.classList.add('is-active');
    document.body.classList.add('filter-panel-open');
    this.filterOpenBtn?.setAttribute('aria-expanded', 'true');

    // Focus the close button for accessibility
    setTimeout(() => {
      this.filterCloseBtn?.focus();
    }, 100);
  }

  closeFilterPanel() {
    if (!this.filterPanel) return;

    this.filterPanel.classList.remove('is-open');
    this.filterOverlay?.classList.remove('is-active');
    document.body.classList.remove('filter-panel-open');
    this.filterOpenBtn?.setAttribute('aria-expanded', 'false');

    // Return focus to open button
    this.filterOpenBtn?.focus();
  }

  // =====================================================
  // CUSTOM DROPDOWNS
  // =====================================================

  initCustomDropdowns() {
    const dropdowns = document.querySelectorAll('.custom-dropdown');

    dropdowns.forEach((dropdown) => {
      const trigger = dropdown.querySelector('.custom-dropdown__trigger');
      const panel = dropdown.querySelector('.custom-dropdown__panel');
      const options = dropdown.querySelectorAll('.custom-dropdown__option');
      const valueDisplay = dropdown.querySelector('.custom-dropdown__value');
      const hiddenSelect = dropdown.querySelector('.custom-dropdown__select');

      if (!trigger || !panel) return;

      // Toggle dropdown on trigger click
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleDropdown(dropdown);
      });

      // Handle option selection
      options.forEach((option) => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.selectOption(dropdown, option, valueDisplay, hiddenSelect);
        });
      });

      // Keyboard navigation
      trigger.addEventListener('keydown', (e) => {
        this.handleDropdownKeyboard(e, dropdown, options);
      });
    });

    // Close all dropdowns on outside click
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.custom-dropdown')) {
        document.querySelectorAll('.custom-dropdown.is-open').forEach((d) => {
          this.closeDropdown(d);
        });
      }
    });
  }

  toggleDropdown(dropdown) {
    const isOpen = dropdown.classList.contains('is-open');

    // Close all other dropdowns
    document.querySelectorAll('.custom-dropdown.is-open').forEach((d) => {
      if (d !== dropdown) {
        this.closeDropdown(d);
      }
    });

    if (isOpen) {
      this.closeDropdown(dropdown);
    } else {
      this.openDropdown(dropdown);
    }
  }

  openDropdown(dropdown) {
    dropdown.classList.add('is-open');
    const trigger = dropdown.querySelector('.custom-dropdown__trigger');
    trigger?.setAttribute('aria-expanded', 'true');
  }

  closeDropdown(dropdown) {
    dropdown.classList.remove('is-open');
    const trigger = dropdown.querySelector('.custom-dropdown__trigger');
    trigger?.setAttribute('aria-expanded', 'false');
  }

  selectOption(dropdown, option, valueDisplay, hiddenSelect) {
    const value = option.dataset.value;
    const label = option.textContent.trim();

    // Update visual state
    dropdown.querySelectorAll('.custom-dropdown__option').forEach((opt) => {
      opt.classList.remove('is-selected');
    });
    option.classList.add('is-selected');

    // Update displayed value
    if (valueDisplay) {
      valueDisplay.textContent = label;
    }

    // Update hidden select and trigger change event
    if (hiddenSelect) {
      hiddenSelect.value = value;
      hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Close dropdown
    this.closeDropdown(dropdown);
  }

  handleDropdownKeyboard(e, dropdown, options) {
    const isOpen = dropdown.classList.contains('is-open');

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.toggleDropdown(dropdown);
        break;
      case 'Escape':
        if (isOpen) {
          e.preventDefault();
          this.closeDropdown(dropdown);
        }
        break;
      case 'ArrowDown':
        if (isOpen) {
          e.preventDefault();
          this.focusNextOption(options);
        }
        break;
      case 'ArrowUp':
        if (isOpen) {
          e.preventDefault();
          this.focusPrevOption(options);
        }
        break;
    }
  }

  focusNextOption(options) {
    const optionsArray = Array.from(options);
    const current = optionsArray.findIndex((opt) =>
      opt === document.activeElement || opt.classList.contains('is-focused')
    );
    const next = current < optionsArray.length - 1 ? current + 1 : 0;
    optionsArray[next]?.focus();
  }

  focusPrevOption(options) {
    const optionsArray = Array.from(options);
    const current = optionsArray.findIndex((opt) =>
      opt === document.activeElement || opt.classList.contains('is-focused')
    );
    const prev = current > 0 ? current - 1 : optionsArray.length - 1;
    optionsArray[prev]?.focus();
  }

  // =====================================================
  // RANGE SLIDERS
  // =====================================================

  initRangeSliders() {
    const sliders = document.querySelectorAll('.range-slider');

    sliders.forEach((slider) => {
      this.initRangeSlider(slider);
    });
  }

  initRangeSlider(slider) {
    const track = slider.querySelector('.range-slider__track');
    const range = slider.querySelector('.range-slider__range');
    const thumbMin = slider.querySelector('.range-slider__thumb--min');
    const thumbMax = slider.querySelector('.range-slider__thumb--max');
    const minInput = slider.querySelector('.range-slider__input--min');
    const maxInput = slider.querySelector('.range-slider__input--max');
    const minValueDisplay = slider.querySelector('[data-min-value]');
    const maxValueDisplay = slider.querySelector('[data-max-value]');

    if (!track || !thumbMin || !thumbMax) return;

    const config = {
      min: parseInt(slider.dataset.min) || 0,
      max: parseInt(slider.dataset.max) || 100,
      step: parseInt(slider.dataset.step) || 1,
      prefix: slider.dataset.prefix || '',
      suffix: slider.dataset.suffix || '',
      formatK: slider.dataset.formatK === 'true',
      formatM: slider.dataset.formatM === 'true',
    };

    const state = {
      minValue: parseInt(slider.dataset.minValue) || config.min,
      maxValue: parseInt(slider.dataset.maxValue) || config.max,
      isDragging: null,
    };

    // Initialize positions
    this.updateSliderUI(slider, state, config, range, thumbMin, thumbMax, minValueDisplay, maxValueDisplay, minInput, maxInput);

    // Thumb drag handlers
    const startDrag = (thumb, type) => (e) => {
      e.preventDefault();
      state.isDragging = type;
      thumb.classList.add('is-dragging');
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    };

    const doDrag = (e) => {
      if (!state.isDragging) return;

      const rect = track.getBoundingClientRect();
      const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      let percent = (clientX - rect.left) / rect.width;
      percent = Math.max(0, Math.min(1, percent));

      const rawValue = config.min + percent * (config.max - config.min);
      const steppedValue = Math.round(rawValue / config.step) * config.step;

      if (state.isDragging === 'min') {
        state.minValue = Math.min(steppedValue, state.maxValue - config.step);
        state.minValue = Math.max(config.min, state.minValue);
      } else {
        state.maxValue = Math.max(steppedValue, state.minValue + config.step);
        state.maxValue = Math.min(config.max, state.maxValue);
      }

      this.updateSliderUI(slider, state, config, range, thumbMin, thumbMax, minValueDisplay, maxValueDisplay, minInput, maxInput);
    };

    const endDrag = () => {
      if (!state.isDragging) return;
      const thumb = state.isDragging === 'min' ? thumbMin : thumbMax;
      thumb.classList.remove('is-dragging');
      state.isDragging = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    // Mouse events
    thumbMin.addEventListener('mousedown', startDrag(thumbMin, 'min'));
    thumbMax.addEventListener('mousedown', startDrag(thumbMax, 'max'));
    document.addEventListener('mousemove', doDrag);
    document.addEventListener('mouseup', endDrag);

    // Touch events
    thumbMin.addEventListener('touchstart', startDrag(thumbMin, 'min'), { passive: false });
    thumbMax.addEventListener('touchstart', startDrag(thumbMax, 'max'), { passive: false });
    document.addEventListener('touchmove', doDrag, { passive: false });
    document.addEventListener('touchend', endDrag);

    // Track click to jump
    track.addEventListener('click', (e) => {
      if (e.target === thumbMin || e.target === thumbMax) return;

      const rect = track.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      const rawValue = config.min + percent * (config.max - config.min);
      const steppedValue = Math.round(rawValue / config.step) * config.step;

      // Determine which thumb to move (closest one)
      const distToMin = Math.abs(steppedValue - state.minValue);
      const distToMax = Math.abs(steppedValue - state.maxValue);

      if (distToMin <= distToMax) {
        state.minValue = Math.min(steppedValue, state.maxValue - config.step);
        state.minValue = Math.max(config.min, state.minValue);
      } else {
        state.maxValue = Math.max(steppedValue, state.minValue + config.step);
        state.maxValue = Math.min(config.max, state.maxValue);
      }

      this.updateSliderUI(slider, state, config, range, thumbMin, thumbMax, minValueDisplay, maxValueDisplay, minInput, maxInput);
    });
  }

  updateSliderUI(slider, state, config, range, thumbMin, thumbMax, minValueDisplay, maxValueDisplay, minInput, maxInput) {
    const minPercent = ((state.minValue - config.min) / (config.max - config.min)) * 100;
    const maxPercent = ((state.maxValue - config.min) / (config.max - config.min)) * 100;

    // Update thumb positions
    thumbMin.style.left = `${minPercent}%`;
    thumbMax.style.left = `${maxPercent}%`;

    // Update range highlight
    range.style.left = `${minPercent}%`;
    range.style.width = `${maxPercent - minPercent}%`;

    // Update value displays
    const formatValue = (value) => {
      let formatted = value.toString();
      if (config.formatM && value >= 1000000) {
        formatted = (value / 1000000).toFixed(1).replace('.0', '') + 'M';
      } else if (config.formatK && value >= 1000) {
        formatted = (value / 1000).toFixed(0) + 'K';
      } else {
        formatted = value.toLocaleString();
      }
      return config.prefix + formatted + config.suffix;
    };

    if (minValueDisplay) {
      minValueDisplay.textContent = formatValue(state.minValue);
    }
    if (maxValueDisplay) {
      maxValueDisplay.textContent = formatValue(state.maxValue);
    }

    // Update hidden inputs
    if (minInput) {
      minInput.value = state.minValue;
    }
    if (maxInput) {
      maxInput.value = state.maxValue;
    }
  }

  // =====================================================
  // EXISTING FUNCTIONALITY
  // =====================================================

  handleSort(value) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort', value);
    window.location.href = url.toString();
  }

  setViewMode(mode) {
    if (!this.propertyGrid) return;

    // Update buttons
    this.viewButtons.forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.view === mode);
    });

    // Update grid class
    if (mode === 'list') {
      this.propertyGrid.classList.add('property-archive__grid--list');
    } else {
      this.propertyGrid.classList.remove('property-archive__grid--list');
    }

    // Update URL without reload
    const url = new URL(window.location.href);
    url.searchParams.set('view', mode);
    window.history.replaceState({}, '', url.toString());

    // Save preference
    localStorage.setItem('tre_property_view', mode);
  }

  restoreViewMode() {
    const saved = localStorage.getItem('tre_property_view');
    if (saved && this.propertyGrid) {
      this.setViewMode(saved);
    }
  }

  async copyToClipboard(url) {
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      this.showToast('Link copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();

      try {
        document.execCommand('copy');
        this.showToast('Link copied to clipboard!');
      } catch (e) {
        console.error('Failed to copy:', e);
      }

      document.body.removeChild(textArea);
    }
  }

  showToast(message) {
    // Create toast element
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%) translateY(100px);
      padding: 12px 24px;
      background-color: #1a1a1a;
      color: #fff;
      border-radius: 8px;
      font-size: 14px;
      z-index: 9999;
      opacity: 0;
      transition: transform 0.3s ease, opacity 0.3s ease;
    `;

    document.body.appendChild(toast);

    // Animate in
    requestAnimationFrame(() => {
      toast.style.transform = 'translateX(-50%) translateY(0)';
      toast.style.opacity = '1';
    });

    // Remove after delay
    setTimeout(() => {
      toast.style.transform = 'translateX(-50%) translateY(100px)';
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }

  // =====================================================
  // FILTER CHIPS
  // =====================================================

  initFilterChips() {
    // Handle individual chip removal
    document.querySelectorAll('[data-remove-filter]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const filterParam = btn.dataset.removeFilter;
        this.removeFilter(filterParam);
      });
    });

    // Handle "Clear All" button
    const clearAllBtn = document.querySelector('[data-clear-all-filters]');
    clearAllBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      this.clearAllFilters();
    });
  }

  removeFilter(filterParam) {
    const url = new URL(window.location.href);
    const chip = document.querySelector(`[data-filter-chip="${filterParam}"]`);

    // Add exit animation
    if (chip) {
      chip.classList.add('is-removing');
    }

    // Handle range filters (price and area have two params each)
    if (filterParam === 'price') {
      url.searchParams.delete('min_price');
      url.searchParams.delete('max_price');
    } else if (filterParam === 'area') {
      url.searchParams.delete('min_area');
      url.searchParams.delete('max_area');
    } else {
      url.searchParams.delete(filterParam);
    }

    // Reset to page 1 when filters change
    url.searchParams.delete('paged');

    // Wait for animation then navigate
    setTimeout(() => {
      window.location.href = url.toString();
    }, 250);
  }

  clearAllFilters() {
    const chips = document.querySelectorAll('[data-filter-chip]');
    const archiveUrl = this.filterForm?.getAttribute('action') || window.location.pathname;

    // Animate all chips out with staggered delay
    chips.forEach((chip, index) => {
      setTimeout(() => {
        chip.classList.add('is-removing');
      }, index * 50);
    });

    // Navigate after animations complete
    setTimeout(() => {
      // Preserve only view and sort params
      const url = new URL(window.location.href);
      const view = url.searchParams.get('view');
      const sort = url.searchParams.get('sort');

      const newUrl = new URL(archiveUrl, window.location.origin);
      if (view) newUrl.searchParams.set('view', view);
      if (sort) newUrl.searchParams.set('sort', sort);

      window.location.href = newUrl.toString();
    }, chips.length * 50 + 250);
  }

  // =====================================================
  // AJAX FILTERING
  // =====================================================

  initAjaxFiltering() {
    if (!this.filterForm) return;

    // Intercept form submission for instant filtering
    this.filterForm.addEventListener('submit', (e) => {
      e.preventDefault();
      this.applyFilters();
    });

    // Auto-apply filters on dropdown change
    document.querySelectorAll('.custom-dropdown').forEach((dropdown) => {
      const hiddenSelect = dropdown.querySelector('.custom-dropdown__select');
      if (hiddenSelect) {
        // Create a MutationObserver to watch for value changes
        const observer = new MutationObserver(() => {
          // Debounce the filter application
          clearTimeout(this.filterTimeout);
          this.filterTimeout = setTimeout(() => {
            this.applyFilters();
          }, 300);
        });

        // We can't observe value changes directly, so we'll use a custom event
        hiddenSelect.addEventListener('change', () => {
          clearTimeout(this.filterTimeout);
          this.filterTimeout = setTimeout(() => {
            this.applyFilters();
          }, 300);
        });
      }
    });
  }

  applyFilters() {
    if (!this.filterForm) return;

    const formData = new FormData(this.filterForm);
    const url = new URL(this.filterForm.action || window.location.pathname, window.location.origin);

    // Build URL params from form data
    for (const [key, value] of formData.entries()) {
      if (value && value !== '') {
        // Skip default values for range sliders
        if (key === 'min_price' && parseInt(value) === this.defaults.min_price) continue;
        if (key === 'max_price' && parseInt(value) === this.defaults.max_price) continue;
        if (key === 'min_area' && parseInt(value) === this.defaults.min_area) continue;
        if (key === 'max_area' && parseInt(value) === this.defaults.max_area) continue;

        url.searchParams.set(key, value);
      }
    }

    // Preserve view mode
    const currentUrl = new URL(window.location.href);
    const view = currentUrl.searchParams.get('view');
    const sort = currentUrl.searchParams.get('sort');
    if (view) url.searchParams.set('view', view);
    if (sort) url.searchParams.set('sort', sort);

    // Reset to page 1
    url.searchParams.delete('paged');

    // Navigate to filtered URL
    window.location.href = url.toString();
  }

  // Helper to update dropdown value programmatically
  updateDropdownValue(filterName, value) {
    const dropdown = document.querySelector(`[data-filter="${filterName}"]`);
    if (!dropdown) return;

    const hiddenSelect = dropdown.querySelector('.custom-dropdown__select');
    const valueDisplay = dropdown.querySelector('.custom-dropdown__value');
    const options = dropdown.querySelectorAll('.custom-dropdown__option');

    if (hiddenSelect) {
      hiddenSelect.value = value;
    }

    // Update visual state
    options.forEach((option) => {
      const isSelected = option.dataset.value === value;
      option.classList.toggle('is-selected', isSelected);
      if (isSelected && valueDisplay) {
        valueDisplay.textContent = option.textContent.trim();
      }
    });
  }

  // Helper to update range slider values
  updateRangeSlider(element, minValue, maxValue) {
    const slider = element.closest('.range-slider');
    if (!slider) return;

    const minInput = slider.querySelector('.range-slider__input--min');
    const maxInput = slider.querySelector('.range-slider__input--max');

    if (minInput) minInput.value = minValue;
    if (maxInput) maxInput.value = maxValue;

    // Trigger visual update (you'd need to also update the thumb positions here)
  }

  destroy() {
    // Cleanup if needed
    clearTimeout(this.filterTimeout);
  }
}
